class Accordion extends HTMLElement {
  constructor() {
    super();
    this.sectionElements = [];
    this.transitionEndHandlers = new WeakMap();
    this.onRootClick = this.onRootClick.bind(this);
    this.onAnchorClick = this.onAnchorClick.bind(this);
  }

  connectedCallback() {
    this.sectionElements = Array.from(this.querySelectorAll('.accordion-wrapper'));
    this.initializeSections();
    this.addEventListener('click', this.onRootClick);
    document.addEventListener('click', this.onAnchorClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onRootClick);
    document.removeEventListener('click', this.onAnchorClick, true);

    this.sectionElements.forEach((sectionEl) => {
      const panelEl = sectionEl.querySelector('.accordion-content');
      if (panelEl) this.removeTransitionEndHandler(panelEl);
    });
  }

  initializeSections() {
    this.sectionElements.forEach((sectionEl) => {
      const buttonEl = sectionEl.querySelector('.accordion-button');
      const panelEl = sectionEl.querySelector('.accordion-content');
      const isOpen =
        sectionEl.classList.contains('is-open') ||
        sectionEl.getAttribute('aria-expanded') === 'true' ||
        (buttonEl && buttonEl.getAttribute('aria-expanded') === 'true');
      sectionEl.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (buttonEl) buttonEl.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      if (panelEl) {
        if (isOpen) {
          panelEl.style.height = 'auto';
          panelEl.style.opacity = '1';
        } else {
          panelEl.style.height = '0px';
          panelEl.style.opacity = '0';
        }
      }
    });
  }

  onRootClick(event) {
    const buttonEl = event.target.closest('.accordion-button');
    if (!buttonEl || !this.contains(buttonEl)) return;

    const sectionEl = buttonEl.closest('.accordion-wrapper');
    const isOpen = buttonEl.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeSection(sectionEl);
    } else {
      this.sectionElements.forEach((s) => {
        if (s !== sectionEl) this.closeSection(s);
      });
      this.openSection(sectionEl);
    }
  }

  openSection(sectionEl) {
    const buttonEl = sectionEl.querySelector('.accordion-button');
    const panelEl = sectionEl.querySelector('.accordion-content');
    if (!buttonEl || !panelEl) return;

    this.removeTransitionEndHandler(panelEl);

    sectionEl.classList.add('is-open');
    sectionEl.setAttribute('aria-expanded', 'true');
    buttonEl.setAttribute('aria-expanded', 'true');

    const startHeight = panelEl.offsetHeight;
    const targetHeight = panelEl.scrollHeight;

    panelEl.style.height = startHeight + 'px';
    panelEl.style.opacity = '1';
    panelEl.getBoundingClientRect();
    panelEl.style.height = targetHeight + 'px';

    const onTransitionEnd = (ev) => {
      if (ev.propertyName !== 'height') return;
      panelEl.style.height = 'auto';
      this.removeTransitionEndHandler(panelEl);
    };

    panelEl.addEventListener('transitionend', onTransitionEnd);
    this.transitionEndHandlers.set(panelEl, onTransitionEnd);
  }

  closeSection(sectionEl) {
    const buttonEl = sectionEl.querySelector('.accordion-button');
    const panelEl = sectionEl.querySelector('.accordion-content');
    if (!buttonEl || !panelEl) return;

    if (buttonEl.getAttribute('aria-expanded') === 'false') return;

    this.removeTransitionEndHandler(panelEl);

    sectionEl.classList.remove('is-open');
    sectionEl.setAttribute('aria-expanded', 'false');
    buttonEl.setAttribute('aria-expanded', 'false');

    const currentHeight = panelEl.scrollHeight;
    panelEl.style.height = currentHeight + 'px';

    panelEl.getBoundingClientRect();

    panelEl.style.height = '0px';
    panelEl.style.opacity = '0';
  }

  removeTransitionEndHandler(panelEl) {
    const prevHandler = this.transitionEndHandlers.get(panelEl);
    if (prevHandler) {
      panelEl.removeEventListener('transitionend', prevHandler);
      this.transitionEndHandlers.delete(panelEl);
    }
  }

  onAnchorClick(e) {
    const a = e.target.closest('a[href]');
    if (!a) return;

    const rawHref = a.getAttribute('href');
    if (!rawHref) return;
    let url;

    try { url = new URL(rawHref, window.location.href); } catch { return; }

    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

    const targetId = decodeURIComponent(url.hash.replace(/^#/, ''));
    if (!targetId || targetId !== this.id) return;

    setTimeout(() => {
      const sectionEl = this.sectionElements[0];
      if (!sectionEl) return;

      const btn = sectionEl.querySelector('.accordion-button');
      const isOpen = btn?.getAttribute('aria-expanded') === 'true';
      if (!isOpen) {
        this.sectionElements.forEach((s) => { if (s !== sectionEl) this.closeSection(s); });
        this.openSection(sectionEl);
        try { btn?.focus({ preventScroll: true }); } catch {}
      }
    }, 0);
  }
}

if (!customElements.get('accordion-section')) {
  customElements.define('accordion-section', Accordion);
}
